import { Episode } from "../../../models/episode.model";
import { Season } from "../../../models/season.model";
import { SeriesProfile } from "../../../models/series-profile.model";
import { Poster } from "../../../types/Poster";

export interface SeasonError {
  seasonIndex: number;
  seasonTitle: string;
  errors: string[];
}

export interface EpisodeError {
  seasonIndex: number;
  episodeIndex: number;
  seasonTitle: string;
  episodeTitle: string;
  errors: string[];
}

export interface ValidationResult {
  isValid: boolean;
  firstError: string | null;
  episodeErrors: EpisodeError[];
  seasonErrors: SeasonError[];
  seriesErrors: string[];
}

const requiredSeriesFields: (keyof SeriesProfile)[] = [
  "title",
  "english_title",
  "custom_name",
  "language",
  "genre",
  "language_rating",
  "adult_content_rating",
  "hateful_rating",
  "drug_rating",
  "violence_rating",
  "harmful_rating",
];

const DIRECTOR_ROLES = new Set(["director"]);
const PRODUCER_ROLES = new Set(["producer"]);

const normalizeRole = (role: string | undefined) => role?.trim().toLowerCase() ?? "";

export const hasPosterTypeZero = (posters: Poster[] | undefined) =>
  Boolean(posters?.some((poster) => poster.type === 0));

const hasCrewRole = (episode: Episode, roles: Set<string>) =>
  episode.crew?.some(
    (member) => roles.has(normalizeRole(member.role)) && Boolean(member.name?.trim())
  ) ?? false;

export const getSeasonErrors = (season: Season): string[] => {
  const errors: string[] = [];

  if (!season.season_title) errors.push("Season title is required");
  if (!season.season_synopsis) errors.push("Synopsis is required");
  if (!season.release_status) errors.push("Release status is required");
  if (season.release_status === "released") {
    if (!season.year_of_release) errors.push("Year of release is required");
    if (!season.medium_of_release) errors.push("Medium of release is required");
    if (!season.release_platform) errors.push("Release platform is required");
  }
  if (!hasPosterTypeZero(season.vertical_poster)) {
    errors.push("Vertical poster (3:4) is required");
  }
  if (!hasPosterTypeZero(season.horizontal_poster)) {
    errors.push("Horizontal poster (16:9) is required");
  }

  return errors;
};

export const getEpisodeErrors = (episode: Episode): string[] => {
  const errors: string[] = [];

  if (!episode.synopsis) errors.push("Synopsis is required");
  if (!episode.shooting_format) errors.push("Shooting format is required");
  if (!episode.aspect_ratio) errors.push("Aspect ratio is required");
  if (!hasPosterTypeZero(episode.vertical_poster)) {
    errors.push("Vertical poster (3:4) is required");
  }
  if (!hasPosterTypeZero(episode.horizontal_poster)) {
    errors.push("Horizontal poster (16:9) is required");
  }
  if (!hasCrewRole(episode, DIRECTOR_ROLES)) {
    errors.push("At least one director is required");
  }
  if (!hasCrewRole(episode, PRODUCER_ROLES)) {
    errors.push("At least one producer is required");
  }
  if (
    episode.cast?.some(
      (member) => member.name?.trim() && !member.character?.trim()
    )
  ) {
    errors.push("Please enter a character name!");
  }

  return errors;
};

export const tabForEpisodeErrors = (errors: string[]): 0 | 1 | 2 => {
  if (
    errors.some((error) => /character name|director|producer/i.test(error))
  ) {
    return 2;
  }
  if (errors.some((error) => /poster/i.test(error))) return 1;
  return 0;
};

export const validateSeriesDraft = ({
  film,
  synopsis,
  verticalPoster,
  horizontalPoster,
  seasons,
}: {
  film: Partial<SeriesProfile>;
  synopsis: string;
  verticalPoster: Poster[];
  horizontalPoster: Poster[];
  seasons: Season[];
}): ValidationResult => {
  const seriesErrors: string[] = [];

  for (const field of requiredSeriesFields) {
    if (field === "genre") {
      if (!film.genre?.length) seriesErrors.push("Genre is required");
      continue;
    }
    if (!film[field]) {
      const label = field.replaceAll("_", " ");
      seriesErrors.push(`${label[0].toUpperCase()}${label.slice(1)} is required`);
    }
  }

  if (!synopsis) seriesErrors.push("Synopsis is required");
  if (!hasPosterTypeZero(verticalPoster)) {
    seriesErrors.push("Vertical poster (3:4) is required");
  }
  if (!hasPosterTypeZero(horizontalPoster)) {
    seriesErrors.push("Horizontal poster (16:9) is required");
  }

  const seasonErrors: SeasonError[] = [];
  const episodeErrors: EpisodeError[] = [];

  seasons.forEach((season, seasonIndex) => {
    const seasonValidationErrors = getSeasonErrors(season);
    if (seasonValidationErrors.length) {
      seasonErrors.push({
        seasonIndex,
        seasonTitle: season.season_title || `Season ${seasonIndex + 1}`,
        errors: seasonValidationErrors,
      });
    }

    season.episodes.forEach((episode, episodeIndex) => {
      const episodeValidationErrors = getEpisodeErrors(episode);
      if (episodeValidationErrors.length) {
        episodeErrors.push({
          seasonIndex,
          episodeIndex,
          seasonTitle: season.season_title || `Season ${seasonIndex + 1}`,
          episodeTitle: episode.title || `Episode ${episodeIndex + 1}`,
          errors: episodeValidationErrors,
        });
      }
    });
  });

  const allErrors = [
    ...seriesErrors,
    ...seasonErrors.map((err) => `${err.seasonTitle}: ${err.errors[0]}`),
    ...episodeErrors.map(
      (err) => `${err.seasonTitle} - ${err.episodeTitle}: ${err.errors[0]}`
    ),
  ];

  return {
    isValid: allErrors.length === 0,
    firstError: allErrors.length ? allErrors[0] : null,
    episodeErrors,
    seasonErrors,
    seriesErrors,
  };
};
